import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  List,
  ListItem,
  ListItemText,
  Table,
  TableBody,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useContext, useEffect, useState } from "react";
import {
  getBills,
  mergeBills,
  retryBillSync,
  deleteBill,
  Bill,
  syncAllStatus,
  seedTestData,
  SyncStatusResponse,
  SeedTestDataResponse,
} from "../services/bill.service";
import GlobalMessageContext from "src/context/globalMessage.context";
import { StyledTableCell, StyledTableRow } from "src/styles";
import BillTable from "src/components/bill-table.component";
import Search from "src/components/search.component";
import { useStickyState } from "src/util";

const normalizePONumber = (term: string): string =>
  term && !term.toUpperCase().startsWith('PO') ? `PO${term}` : term;

const BillsView = () => {
  const [bills, setBills] = useState<Bill[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedBills, setSelectedBills] = useState<Set<string>>(new Set());
  const [mergeDialogOpen, setMergeDialogOpen] = useState(false);
  const [mergingBills, setMergingBills] = useState<Bill[]>([]);
  const [mergeInProgress, setMergeInProgress] = useState(false);
  const [currentSkip, setCurrentSkip] = useStickyState<number>(0, "bills.currentSkip");
  const [currentTake, setCurrentTake] = useStickyState<number>(25, "bills.currentTake");
  const [poNumberSearch, setPoNumberSearch] = useStickyState<string>("", "bills.poNumberSearch");

  // Delete confirmation state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [billToDelete, setBillToDelete] = useState<Bill | null>(null);

  // Admin tools state
  const [adminLoading, setAdminLoading] = useState(false);
  const [syncStatusResultsOpen, setSyncStatusResultsOpen] = useState(false);
  const [syncStatusResults, setSyncStatusResults] = useState<SyncStatusResponse | null>(null);
  const [seedResultsOpen, setSeedResultsOpen] = useState(false);
  const [seedResults, setSeedResults] = useState<SeedTestDataResponse | null>(null);

  const uxMessage = useContext(GlobalMessageContext);

  useEffect(() => {
    loadBills(poNumberSearch ? normalizePONumber(poNumberSearch) : undefined);
  }, []);

  const loadBills = async (poNumber?: string) => {
    setLoading(true);
    try {
      const response = await getBills({
        limit: 1000,
        ...(poNumber ? { poNumber } : {}),
      });
      setBills(response.bills || []);
    } catch (error: any) {
      uxMessage.setMessage({
        message: `Failed to load bills: ${error.message}`,
        severity: "error",
      });
      setBills([]);
    } finally {
      setLoading(false);
    }
  };

  const setSkip = (page: number) => {
    setCurrentSkip(page * currentTake);
  };

  const setTake = (pageSize: number) => {
    if (pageSize !== currentTake) {
      const oldTake = currentTake;
      setCurrentTake(pageSize);
      setCurrentSkip(Math.trunc(currentSkip * (oldTake / pageSize)));
    }
  };

  const handleSelectBill = (guid: string) => {
    const newSelected = new Set(selectedBills);
    if (newSelected.has(guid)) {
      newSelected.delete(guid);
    } else {
      newSelected.add(guid);
    }
    setSelectedBills(newSelected);
  };

  // Get the supplier name of currently selected bills
  const getSelectedSupplier = (): string | null => {
    if (selectedBills.size === 0) return null;
    const firstSelectedBill = bills.find((b) => selectedBills.has(b.guid));
    return firstSelectedBill?.supplierName || null;
  };

  // Check if a bill can be selected (must be SYNCED and have same supplier as already selected bills)
  const canSelectBill = (bill: Bill): boolean => {
    // Only SYNCED bills can be merged
    if (bill.syncStatus !== 'SYNCED') return false;

    const selectedSupplier = getSelectedSupplier();
    if (!selectedSupplier) return true; // No bills selected yet
    return bill.supplierName === selectedSupplier;
  };

  const handleOpenMergeDialog = () => {
    const selectedBillsList = bills.filter((b) => selectedBills.has(b.guid));
    setMergingBills(selectedBillsList);
    setMergeDialogOpen(true);
  };

  const handleCloseMergeDialog = () => {
    setMergeDialogOpen(false);
    setMergingBills([]);
  };

  const handleConfirmMerge = async () => {
    const billGuids = Array.from(selectedBills);
    const estimatedSeconds = Math.ceil(billGuids.length * 0.7);

    setMergeInProgress(true);

    try {
      uxMessage.setMessage({
        message: `Merging ${billGuids.length} bills... This may take up to ${estimatedSeconds} seconds.`,
        severity: "info",
      });

      await mergeBills(billGuids);

      uxMessage.setMessage({
        message: `Successfully merged ${billGuids.length} bills`,
        severity: "success",
      });

      setSelectedBills(new Set());
      setMergeDialogOpen(false);
      await loadBills(poNumberSearch || undefined);
    } catch (error: any) {
      uxMessage.setMessage({
        message: `Failed to merge bills: ${error.response?.data?.message || error.message}`,
        severity: "error",
      });
    } finally {
      setMergeInProgress(false);
    }
  };

  const canMergeSelection = () => {
    if (!bills || bills.length === 0) return false;
    if (selectedBills.size < 2) return false;

    // Note: All bills in AMS are DRAFT (auto-cleaned on read), so no status check needed
    // Bills can now be merged across different POs as long as they share the same supplier

    return true;
  };

  const handleRetryBillSync = async (billGuid: string) => {
    try {
      const retryingBill = bills.find(b => b.guid === billGuid);
      if (!retryingBill) {
        throw new Error('Bill not found');
      }

      uxMessage.setMessage({
        message: `Retrying sync for bill ${retryingBill.billReference}...`,
        severity: "info",
      });

      await retryBillSync(billGuid);

      uxMessage.setMessage({
        message: `Successfully retried sync for bill ${retryingBill.billReference}`,
        severity: "success",
      });

      // Reload bills to show updated status
      await loadBills(poNumberSearch || undefined);
    } catch (error: any) {
      uxMessage.setMessage({
        message: `Failed to retry bill sync: ${error.response?.data?.message || error.message}`,
        severity: "error",
      });
    }
  };

  const handleDeleteBill = (billGuid: string) => {
    const bill = bills.find(b => b.guid === billGuid);
    if (!bill) {
      uxMessage.setMessage({
        message: 'Bill not found',
        severity: "error",
      });
      return;
    }
    setBillToDelete(bill);
    setDeleteDialogOpen(true);
  };

  const confirmDeleteBill = async () => {
    if (!billToDelete) return;

    setDeleteDialogOpen(false);

    try {
      uxMessage.setMessage({
        message: `Deleting bill ${billToDelete.billReference}...`,
        severity: "info",
      });

      await deleteBill(billToDelete.guid);

      uxMessage.setMessage({
        message: `Successfully deleted bill ${billToDelete.billReference}`,
        severity: "success",
      });

      setBillToDelete(null);
      await loadBills(poNumberSearch || undefined);
    } catch (error: any) {
      uxMessage.setMessage({
        message: `Failed to delete bill: ${error.response?.data?.message || error.message}`,
        severity: "error",
      });
    }
  };

  const cancelDeleteBill = () => {
    setDeleteDialogOpen(false);
    setBillToDelete(null);
  };

  // ============================================================================
  // Admin Tools Handlers
  // ============================================================================

  const handleSyncAllStatus = async () => {
    setAdminLoading(true);
    try {
      const result = await syncAllStatus();
      setSyncStatusResults(result);
      setSyncStatusResultsOpen(true);

      uxMessage.setMessage({
        message: `Sync complete: ${result.updated} updated, ${result.unchanged} unchanged`,
        severity: "success",
      });

      // Reload bills to show updated status
      await loadBills(poNumberSearch || undefined);
    } catch (error: any) {
      uxMessage.setMessage({
        message: `Failed to sync status: ${error.response?.data?.message || error.message}`,
        severity: "error",
      });
    } finally {
      setAdminLoading(false);
    }
  };

  const handleSeedTestData = async () => {
    setAdminLoading(true);
    try {
      const result = await seedTestData();
      setSeedResults(result);
      setSeedResultsOpen(true);

      uxMessage.setMessage({
        message: `Created ${result.created} test bills`,
        severity: "success",
      });

      // Reload bills to show new test data
      await loadBills(poNumberSearch || undefined);
    } catch (error: any) {
      uxMessage.setMessage({
        message: `Failed to seed test data: ${error.response?.data?.message || error.message}`,
        severity: "error",
      });
    } finally {
      setAdminLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: "GBP",
    }).format(amount / 100);
  };

  const totalSelected = selectedBills.size;
  const totalSelectedAmount = bills
    .filter((b) => selectedBills.has(b.guid))
    .reduce((sum, bill) => sum + bill.totalAmount, 0);

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h1" sx={{ mb: 3 }}>
        Bill Management
      </Typography>

      <Box sx={{ mb: 2, maxWidth: 400 }}>
        <Search
          searchTerm={poNumberSearch}
          setSearchTerm={setPoNumberSearch}
          doSearch={(term) => {
            const normalized = normalizePONumber(term);
            setPoNumberSearch(normalized);
            setSelectedBills(new Set());
            loadBills(normalized);
          }}
          onReset={() => {
            setPoNumberSearch("");
            setSelectedBills(new Set());
            loadBills();
          }}
          placeholderText="Search by PO number..."
          clearOnEnter={false}
        />
      </Box>

      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          gap: 2,
          mb: 2,
        }}
      >
        {totalSelected > 0 && (
          <Typography variant="body2" color="textSecondary" sx={{ marginRight: "auto" }}>
            {totalSelected} bill{totalSelected !== 1 ? "s" : ""} selected (
            {formatCurrency(totalSelectedAmount)})
          </Typography>
        )}
        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
          <Button
            variant="contained"
            size="small"
            onClick={handleOpenMergeDialog}
            disabled={!canMergeSelection()}
          >
            Merge Selected Bills ({totalSelected})
          </Button>
          <Button
            variant="contained"
            size="small"
            onClick={handleSyncAllStatus}
            disabled={adminLoading}
            color="secondary"
          >
            {adminLoading ? <CircularProgress size={20} /> : "Sync Status"}
          </Button>
          {import.meta.env.MODE !== "production" && (
            <Button
              variant="contained"
              size="small"
              onClick={handleSeedTestData}
              disabled={adminLoading}
              color="warning"
            >
              {adminLoading ? <CircularProgress size={20} /> : "Seed Test Data"}
            </Button>
          )}
        </Box>
      </Box>

      <BillTable
        data={bills}
        pageSize={currentTake}
        currentPage={Math.floor(currentSkip / currentTake)}
        loading={loading}
        onPageChange={setSkip}
        onPageSizeChange={setTake}
        selectedBills={selectedBills}
        onSelectBill={handleSelectBill}
        canSelectBill={canSelectBill}
        onRetry={handleRetryBillSync}
        onDelete={handleDeleteBill}
      />

      {/* Delete Bill Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={cancelDeleteBill}
        maxWidth="sm"
      >
        <DialogTitle>Confirm Bill Deletion</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete bill{" "}
            <strong>{billToDelete?.billReference}</strong>?
          </DialogContentText>
          <DialogContentText sx={{ mt: 2, color: "warning.main" }}>
            ⚠️ This action cannot be undone. The bill will be deleted from both AMS and Xero.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelDeleteBill} color="inherit">
            Cancel
          </Button>
          <Button onClick={confirmDeleteBill} variant="contained" color="error">
            Delete Bill
          </Button>
        </DialogActions>
      </Dialog>

      {/* Seed Test Data Results Dialog */}
      <Dialog
        open={seedResultsOpen}
        onClose={() => setSeedResultsOpen(false)}
        maxWidth="md"
      >
        <DialogTitle>Seed Test Data Results</DialogTitle>
        <DialogContent>
          {seedResults && (
            <>
              <Typography variant="body1" sx={{ mb: 2 }}>
                ✓ Created {seedResults.created} test bill{seedResults.created !== 1 ? 's' : ''} from real unbilled receipts
              </Typography>

              {seedResults.message && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {seedResults.message}
                </Typography>
              )}

              {seedResults.bills.length > 0 && (
                <>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Test Bills Created:
                  </Typography>
                  <List dense sx={{ maxHeight: 400, overflow: "auto" }}>
                    {seedResults.bills.map((bill) => (
                      <ListItem key={bill.guid}>
                        <ListItemText
                          primary={`${bill.billReference} - ${bill.supplierName}`}
                          secondary={
                            <>
                              PO: {bill.poNumber} •
                              £{bill.totalAmount.toFixed(2)} •
                              {bill.receiptCount} receipt{bill.receiptCount !== 1 ? 's' : ''} •
                              Status: {bill.syncStatus}
                            </>
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                </>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSeedResultsOpen(false)} variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Sync Status Results Dialog */}
      <Dialog
        open={syncStatusResultsOpen}
        onClose={() => setSyncStatusResultsOpen(false)}
        maxWidth="sm"
      >
        <DialogTitle>Sync Status Results</DialogTitle>
        <DialogContent>
          {syncStatusResults && (
            <>
              <Typography variant="body1" sx={{ mb: 2 }}>
                Total bills checked: {syncStatusResults.total}
              </Typography>
              <Typography variant="body1" color="success.main" sx={{ mb: 1 }}>
                ✓ Updated: {syncStatusResults.updated}
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                ⊘ Unchanged: {syncStatusResults.unchanged}
              </Typography>

              {syncStatusResults.errors.length > 0 && (
                <>
                  <Typography variant="body1" color="error.main" sx={{ mb: 1 }}>
                    ✗ Errors: {syncStatusResults.errors.length}
                  </Typography>
                  <List dense sx={{ maxHeight: 200, overflow: "auto" }}>
                    {syncStatusResults.errors.map((err, idx) => (
                      <ListItem key={idx}>
                        <ListItemText
                          primary={err.billRef}
                          secondary={err.error}
                        />
                      </ListItem>
                    ))}
                  </List>
                </>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSyncStatusResultsOpen(false)} variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Merge Confirmation Dialog */}
      <Dialog
        open={mergeDialogOpen}
        onClose={handleCloseMergeDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Confirm Bill Merge</DialogTitle>
        <DialogContent>
          {mergeInProgress ? (
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2, py: 4 }}>
              <CircularProgress size={60} />
              <Typography variant="h6" color="primary">
                Merging {mergingBills.length} bills...
              </Typography>
              <Typography variant="body2" color="text.secondary">
                This may take up to {Math.ceil(mergingBills.length * 0.7)} seconds. Please wait.
              </Typography>
            </Box>
          ) : (
            <>
              <DialogContentText sx={{ mb: 2 }}>
                You are about to merge {mergingBills.length} bills into a single
                consolidated bill. This action cannot be undone.
              </DialogContentText>

          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Bills to be merged:
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <StyledTableCell>Reference</StyledTableCell>
                  <StyledTableCell>Supplier</StyledTableCell>
                  <StyledTableCell>PO Number(s)</StyledTableCell>
                  <StyledTableCell align="right">Amount</StyledTableCell>
                  <StyledTableCell>Receipts</StyledTableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {mergingBills.map((bill) => (
                  <StyledTableRow key={bill.guid}>
                    <StyledTableCell>
                      <Typography variant="body2" fontFamily="monospace">
                        {bill.billReference}
                      </Typography>
                    </StyledTableCell>
                    <StyledTableCell>
                      <Typography variant="body2">
                        {bill.supplierName || "Unknown"}
                      </Typography>
                    </StyledTableCell>
                    <StyledTableCell>
                      <Typography variant="body2">
                        {bill.purchaseOrderNumbers.join(", ")}
                      </Typography>
                    </StyledTableCell>
                    <StyledTableCell align="right">
                      {formatCurrency(bill.totalAmount)}
                    </StyledTableCell>
                    <StyledTableCell>
                      {bill.receiptGuids.length}
                    </StyledTableCell>
                  </StyledTableRow>
                ))}
                <StyledTableRow>
                  <StyledTableCell colSpan={3}>
                    <Typography variant="subtitle2">Total</Typography>
                  </StyledTableCell>
                  <StyledTableCell align="right">
                    <Typography variant="subtitle2" fontWeight="bold">
                      {formatCurrency(
                        mergingBills.reduce((sum, b) => sum + b.totalAmount, 0)
                      )}
                    </Typography>
                  </StyledTableCell>
                  <StyledTableCell>
                    <Typography variant="subtitle2">
                      {mergingBills.reduce(
                        (sum, b) => sum + b.receiptGuids.length,
                        0
                      )}{" "}
                      receipt
                      {mergingBills.reduce(
                        (sum, b) => sum + b.receiptGuids.length,
                        0
                      ) !== 1
                        ? "s"
                        : ""}
                    </Typography>
                  </StyledTableCell>
                </StyledTableRow>
              </TableBody>
            </Table>
          </Box>

          <Box
            sx={{
              p: 2,
              border: "1px solid",
              borderColor: "warning.main",
              borderRadius: 1,
              mb: 2,
            }}
          >
            <Typography variant="body2" color="text.primary">
              ⚠️ The original bills will be deleted from Xero and replaced with
              a single consolidated bill. The merged bill reference will include
              all PO numbers with a unique timestamp (e.g., PO001-PO002-MERGED-250318143052).
            </Typography>
          </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseMergeDialog} color="inherit" disabled={mergeInProgress}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmMerge}
            variant="contained"
            color="primary"
            disabled={mergeInProgress}
            startIcon={mergeInProgress ? <CircularProgress size={20} /> : null}
          >
            {mergeInProgress ? `Merging ${mergingBills.length} bills...` : "Confirm Merge"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BillsView;
